/**
 * P3-03: TabNav —— tab 导航条（迁移自 DesktopUI.render() 的 .tabsContainer 段，
 * js/ui.js:386-435）+ trait 高亮（updateTabs，js/ui.js:663-688）。
 *
 * 结构逐一对齐：
 * - div.tabsContainer 内按 game.tabs 顺序渲染 visible tab：
 *   a.tab.{tabId}（href="#"、whiteSpace:nowrap、innerHTML=tab.tabName），
 *   当前 tab 加 activeTab（js/ui.js:407-419）；tab 间夹 span " | "（js/ui.js:432-434）；
 * - 渲染前对每个 visible tab 调 tab.updateTab()（js/ui.js:406）——它会重算
 *   tabName（如 Village/Workshop 的空闲猫数 <span class='genericWarning'>），
 *   并回写旧 dojo domNode、广播 ui/refreshTabNames（共存期旧 render 仍在跑，
 *   回写打到隐藏节点无害；全仓库无 jsx 订阅该 topic）；
 * - 点击：activeTabId = tabId → game.ui.render() → telemetry.logRouteChange
 *   （js/ui.js:422-429；旧版不 preventDefault，保持一致）；
 * - trait 高亮：village.leader.trait 映射到对应 tab 加 "traitLeaderBonus {trait}"
 *   （engineer/metallurgist/chemist→Workshop、merchant→Trade、manager→Village、
 *   scientist→Science、wise→Religion，js/ui.js:665-687）；无 leader 不加，
 *   等价旧版"先 removeClass 再按 leader 加"的每 tick 语义。
 *
 * 锁/解锁显示：tab.visible 由引擎各 tab 自行维护（evaluateLocks 等），本组件只
 * 消费——未解锁 tab 根本不出现在导航条，与旧版一致。
 *
 * 注册方式：KGNewUI.panels.TabNav（**已注册未启用**，同 P2 各面板做法）；
 * 启用由后续 tab 迁移步骤经 P3-05 共存容器（MidPanel）统一挂载，届时旧
 * .tabsContainer 随 #gameContainerId 显隐切换。
 */
import { Fragment, useCallback } from "react";
import { getGame } from "../bridge/GameBridge";
import type { GamePage } from "../bridge/GameBridge";
import { useGameTick } from "../bridge/useGameTick";

// ---------------------------------------------------------------------------
// 快照
// ---------------------------------------------------------------------------

interface TabEntry {
  tabId: string;
  /** updateTab() 后的最新 tabName（innerHTML 语义，引擎生成） */
  tabName: string;
}

interface TabNavSnapshot {
  activeTabId: string;
  /** village.leader.trait.name；无 leader 为 null */
  leaderTrait: string | null;
  tabs: TabEntry[];
}

/** trait → 高亮目标 tabId（js/ui.js:665-687 的 switch 逐条） */
const TRAIT_TAB: Record<string, string> = {
  engineer: "Workshop",
  metallurgist: "Workshop",
  chemist: "Workshop",
  merchant: "Trade",
  manager: "Village",
  scientist: "Science",
  wise: "Religion",
};

export function computeTabNavSnapshot(game: GamePage | null): TabNavSnapshot | null {
  if (!game) {
    return null;
  }
  const tabs: TabEntry[] = [];
  for (const tab of game.tabs as {
    tabId: string;
    tabName: string;
    visible: boolean;
    updateTab?: () => void;
  }[]) {
    // 旧版顺序：先按 visible 收集（js/ui.js:395-401），渲染前再 updateTab（js/ui.js:406）
    if (!tab.visible) {
      continue;
    }
    tab.updateTab?.();
    tabs.push({ tabId: tab.tabId, tabName: tab.tabName });
  }
  const leader = game.village?.leader as { trait?: { name?: string } } | null;
  return {
    activeTabId: game.ui.activeTabId as string,
    leaderTrait: leader?.trait?.name ?? null,
    tabs,
  };
}

function snapshotEq(a: TabNavSnapshot | null, b: TabNavSnapshot | null): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  if (a.activeTabId !== b.activeTabId || a.leaderTrait !== b.leaderTrait || a.tabs.length !== b.tabs.length) {
    return false;
  }
  for (let i = 0; i < a.tabs.length; i++) {
    if (a.tabs[i].tabId !== b.tabs[i].tabId || a.tabs[i].tabName !== b.tabs[i].tabName) {
      return false;
    }
  }
  return true;
}

// ---------------------------------------------------------------------------
// 组件
// ---------------------------------------------------------------------------

/**
 * 面板入口。KGNewUI.panels.TabNav 已注册，本阶段不挂载（页面行为与现状零差异）；
 * 启用路径见 P3-05 MidPanel。
 */
export default function TabNav() {
  const selector = useCallback((game: GamePage | null) => computeTabNavSnapshot(game), []);
  const snap = useGameTick(selector, snapshotEq);

  const onTabClick = useCallback((tabId: string) => {
    const game = getGame();
    if (!game) {
      return;
    }
    // js/ui.js:422-429
    game.ui.activeTabId = tabId;
    game.ui.render();
    game.telemetry.logRouteChange(tabId);
  }, []);

  if (!snap) {
    return null;
  }

  const traitTabId = snap.leaderTrait ? TRAIT_TAB[snap.leaderTrait] : undefined;

  return (
    <div className="tabsContainer">
      {snap.tabs.map((tab, i) => {
        const className =
          "tab " +
          tab.tabId +
          (snap.activeTabId === tab.tabId ? " activeTab" : "") +
          (traitTabId === tab.tabId ? " traitLeaderBonus " + snap.leaderTrait : "");
        return (
          <Fragment key={tab.tabId}>
            <a
              href="#"
              className={className}
              style={{ whiteSpace: "nowrap" }}
              onClick={() => onTabClick(tab.tabId)}
              dangerouslySetInnerHTML={{ __html: tab.tabName }}
            />
            {i < snap.tabs.length - 1 && <span>{" | "}</span>}
          </Fragment>
        );
      })}
    </div>
  );
}
